import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Row,
    Col,
    Tree,
    Button,
    Breadcrumb,
    Icon,
    Popconfirm,
} from 'antd';

import { Section } from '../../store/api/apiTypes';
import {
    list, del, SectionsState, getSectionsListState,
    getSectionDeleteState, getSectionCreateState, getSectionUpdateState,
} from '../../store/actions/sections';
import { MainReducerState } from '../../store/reducers';
import { canManageFiles } from '../../utils';
import { getRoute, RoutePathName } from '../../routes';
import { update } from '../../store/actions/documents';
import { getUser, CheckLoginStatusState } from '../../store/actions/auth';
import SectionDrawer from './SectionDrawer';

const { TreeNode } = Tree;

interface SectionsProps {
    created: SectionsState['create'];
    deleted: SectionsState['delete'];
    updated: SectionsState['update'];
    deleteSection: typeof del.trigger;
    loadSections: typeof list.trigger;
    sectionsState: SectionsState['list'];
    user: CheckLoginStatusState['user'];
}

const Sections: FC<SectionsProps> = ({
    created,
    deleted,
    updated,
    deleteSection,
    loadSections,
    sectionsState,
    user,
}) => {

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [addDisabled, setAddDisabled] = useState(false);
    const [selectedId, setSelectedId] = useState<string>();
    const [action, setAction] = useState<string>();

    useEffect(() => {
        loadSections({
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (created.success || deleted.success || updated.success) {
            loadSections({
            });
        }
    }, [created.success, deleted.success, updated.success]); // eslint-disable-line react-hooks/exhaustive-deps

    const onEdit = () => {
        setAction('edit');
        setIsFormVisible(true);
    };

    const onAdd = () => {
        setAction('add');
        setIsFormVisible(true);
    };

    const onDrawerClose = () => {
        setIsFormVisible(false);
    };

    const onDeleteElement = () => {
        deleteSection({ id: selectedId });
    };

    const onSelect = (selectedKeys: string[], info: any) => {
        if (selectedKeys.length === 1) {
            setSelectedId(selectedKeys[0]);
        } else {
            setSelectedId(undefined);
        }
        if (info.node.props.children === undefined) {
            setAddDisabled(true);
        } else {
            setAddDisabled(false);
        }
    };

    return (
        <>
            {sectionsState && sectionsState.data && (
                <>
                    <Row align="middle" gutter={[8, 20]}>
                        <Col span={24}>
                            <Breadcrumb>
                                <Breadcrumb.Item href={getRoute(RoutePathName.documents, {})} >
                                    <Icon type="home" />
                                </Breadcrumb.Item>
                                <Breadcrumb.Item href={getRoute(RoutePathName.sections, {})}>
                                    <Icon type="setting" />
                                    <span>Rubriques</span>
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </Col>
                    </Row>
                    <Row gutter={[8, 20]}>
                        <Col span={24} push={2}>
                            Sélectionnez une rubrique pour la modifier, ou pour y ajouter une sous-rubrique.
                        </Col>
                    </Row>
                    <Row gutter={[8, 20]}>
                        <Col span={24}>
                            <Tree
                                showIcon
                                defaultExpandAll={true}
                                onSelect={onSelect}
                            >
                                {sectionsState.data && sectionsState.data.items.map((section: Section) => (
                                    <TreeNode
                                        title={section.short}
                                        key={section.id}
                                        icon={<Icon type={section.picto} />}
                                    >
                                        {section.children && section.children.map((sub: Section) => (
                                            <TreeNode
                                                title={sub.short}
                                                key={sub.id}
                                                icon={<Icon type={sub.picto} />}
                                            />
                                        ))}
                                    </TreeNode>
                                ))}
                            </Tree>
                        </Col>
                    </Row>
                    <Row gutter={[8, 40]}>
                        <Col xs={1} sm={2} md={2} lg={2} xl={2}>
                            {canManageFiles(user) && (
                                <Button
                                    type="primary"
                                    icon="plus-circle"
                                    onClick={onAdd}
                                    disabled={addDisabled}
                                >
                                    Ajouter une rubrique
                                </Button>
                            )}
                        </Col>
                        <Col xs={1} sm={2} md={2} lg={2} xl={2} push={3}>
                            {canManageFiles(user) && (
                                <Button
                                    type="primary"
                                    icon="edit"
                                    onClick={onEdit}
                                    disabled={selectedId === undefined}
                                >
                                    Modifier une rubrique
                                </Button>
                            )}
                        </Col>
                        <Col xs={1} sm={2} md={2} lg={2} xl={2} push={7}>
                            {canManageFiles(user) && (
                                <Popconfirm
                                    title="Voulez-vous vraiment supprimer?"
                                    onConfirm={onDeleteElement}
                                    okText="Oui"
                                    cancelText="Non"
                                >
                                    <Button
                                        type="danger"
                                        icon="delete"
                                        disabled={selectedId === undefined}
                                    >
                                        Supprimer une rubrique
                                    </Button>
                                </Popconfirm>
                            )}
                        </Col>
                    </Row>
                </>
            )}
            <SectionDrawer
                isVisible={isFormVisible}
                onClose={onDrawerClose}
                sectionId={selectedId}
                action={action}
            />
        </>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    created: getSectionCreateState(state),
    deleted: getSectionDeleteState(state),
    updated: getSectionUpdateState(state),
    sectionsState: getSectionsListState(state),
    user: getUser(state),
});

export default connect(
    mapStateToProps,
    {
        loadSections: list.trigger,
        deleteSection: del.trigger,
        updateSection: update.trigger,
    },
)(Sections);
